import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import { C, S, getStatusBadgeStyle, formatDate } from '../styles';

function StatCard({ label, value, color, bgColor, onClick }) {
  return (
    <div
      style={{
        ...S.card,
        textAlign: 'center',
        padding: '20px 16px',
        background: bgColor || S.card.background,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <div style={{ fontSize: '32px', fontWeight: '700', color: color || C.text }}>
        {value}
      </div>
      <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
    </div>
  );
}

function ActionItem({ title, subtitle, status, onClick }) {
  const badgeStyle = getStatusBadgeStyle(status);
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: `1px solid ${C.border}`,
        cursor: 'pointer',
        transition: 'background 0.1s',
        gap: '12px',
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: '500', fontSize: '14px', color: C.text, marginBottom: '2px' }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: '12px', color: C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {subtitle}
          </div>
        )}
      </div>
      {status && (
        <span style={{ ...S.badge, ...badgeStyle, flexShrink: 0 }}>
          {status.replace('_', ' ')}
        </span>
      )}
      <span style={{ color: C.textLight, fontSize: '16px', flexShrink: 0 }}>›</span>
    </div>
  );
}

function SectionCard({ title, children, emptyMsg }) {
  return (
    <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, background: '#f9fafb' }}>
        <h3 style={{ ...S.cardTitle, margin: 0 }}>{title}</h3>
      </div>
      {children || (
        <div style={{ padding: '24px 16px', textAlign: 'center', color: C.textMuted, fontSize: '14px' }}>
          {emptyMsg || 'Nothing to show'}
        </div>
      )}
    </div>
  );
}

export default function Dashboard({ navigate }) {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [showRefreshBtn, setShowRefreshBtn] = useState(false);
  const [data, setData] = useState({
    projects: [],
    openInstructions: [],
    draftDayworks: [],
    submittedDayworks: [],
    disputedDayworks: [],
    variations: [],
    pendingApprovals: [],
    awaitingMcCount: 0,
    mcRespondedCount: 0,
    draftInstructionsCount: 0,
    raisedByMcCount: 0,
  });

  const role = profile?.role || '';

  useEffect(() => {
    setRetryCount(0);
    setShowRefreshBtn(false);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // Silent auto-retry every 2s while loading, up to 5 attempts, then show manual refresh button.
  useEffect(() => {
    if (!profile || !loading) return;
    if (retryCount >= 5) {
      setShowRefreshBtn(true);
      return;
    }
    const timeout = setTimeout(() => {
      loadData();
      setRetryCount(c => c + 1);
    }, 2000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, retryCount, profile]);

  async function loadData() {
    if (!profile) return;
    setLoading(true);
    try {
      const result = {
        projects: [],
        openInstructions: [],
        draftDayworks: [],
        submittedDayworks: [],
        disputedDayworks: [],
        variations: [],
        variationsToPrice: [],
        pendingApprovals: [],
        awaitingMcCount: 0,
        mcRespondedCount: 0,
        draftInstructionsCount: 0,
        raisedByMcCount: 0,
      };

      if (role === 'admin') {
        // Admins see everything — no project_users filter
        const [
          { data: allProjects },
          { data: instructions },
          { data: draftDW },
          { data: disputed },
          { data: vars },
          { data: pending },
          { count: awaitingMc },
          { count: mcResponded },
          { count: draftIns },
          { count: raisedByMc },
        ] = await Promise.all([
          supabase.from('projects').select('id, name, reference').eq('active', true).order('name'),
          supabase.from('instructions')
            .select('id, reference, type, description, status, project_id, projects(name)')
            .eq('status', 'open').order('created_at', { ascending: false }).limit(10),
          supabase.from('dayworks')
            .select('id, reference, date_of_works, status, project_id, projects(name)')
            .eq('status', 'draft').order('created_at', { ascending: false }).limit(10),
          supabase.from('dayworks')
            .select('id, reference, date_of_works, status, dispute_reason, project_id, projects(name)')
            .eq('status', 'disputed').order('created_at', { ascending: false }).limit(10),
          supabase.from('variations')
            .select('id, reference, title, status, value, project_id, projects(name)')
            .not('status', 'eq', 'agreed').order('created_at', { ascending: false }).limit(10),
          supabase.from('profiles')
            .select('id, full_name, email, role, company')
            .eq('approved', false).order('created_at', { ascending: false }),
          supabase.from('instructions').select('id', { count: 'exact', head: true })
            .in('type', ['EWN', 'CVI']).eq('status', 'awaiting_instruction'),
          supabase.from('instructions').select('id', { count: 'exact', head: true })
            .in('type', ['EWN', 'CVI']).not('mc_response', 'is', null).eq('status', 'awaiting_instruction'),
          supabase.from('instructions').select('id', { count: 'exact', head: true })
            .eq('status', 'draft'),
          supabase.from('instructions').select('id', { count: 'exact', head: true })
            .eq('raised_by_mc', true),
        ]);
        const { data: varsToPrice } = await supabase.from('variations')
          .select('id, reference, title, status, project_id, raised_at, projects(name)')
          .eq('status', 'qs_review').order('raised_at', { ascending: true });
        result.projects = allProjects || [];
        result.openInstructions = instructions || [];
        result.draftDayworks = draftDW || [];
        result.disputedDayworks = disputed || [];
        result.variations = vars || [];
        result.variationsToPrice = varsToPrice || [];
        result.pendingApprovals = pending || [];
        result.awaitingMcCount = awaitingMc || 0;
        result.mcRespondedCount = mcResponded || 0;
        result.draftInstructionsCount = draftIns || 0;
        result.raisedByMcCount = raisedByMc || 0;
      } else {
        // Non-admin: scope to assigned projects only
        const { data: projectUsers } = await supabase
          .from('project_users')
          .select('project_id, projects(id, name, reference)')
          .eq('user_id', profile.id);

        const myProjectIds = (projectUsers || []).map(pu => pu.project_id);
        result.projects = (projectUsers || []).map(pu => pu.projects).filter(Boolean);

        if (myProjectIds.length > 0) {
          if (role === 'proline_sm') {
            const [{ data: instructions }, { data: draftDW }] = await Promise.all([
              supabase.from('instructions')
                .select('id, reference, type, description, status, project_id, projects(name)')
                .in('project_id', myProjectIds).eq('status', 'open')
                .order('created_at', { ascending: false }).limit(10),
              supabase.from('dayworks')
                .select('id, reference, date_of_works, status, project_id, projects(name)')
                .in('project_id', myProjectIds).eq('status', 'draft')
                .order('created_at', { ascending: false }).limit(10),
            ]);
            result.openInstructions = instructions || [];
            result.draftDayworks = draftDW || [];
          }

          if (role === 'proline_qs') {
            const [{ data: vars }, { data: varsToPrice }] = await Promise.all([
              supabase.from('variations')
                .select('id, reference, title, status, value, project_id, projects(name)')
                .in('project_id', myProjectIds).not('status', 'eq', 'agreed')
                .not('status', 'eq', 'qs_review')
                .order('created_at', { ascending: false }).limit(10),
              supabase.from('variations')
                .select('id, reference, title, status, project_id, raised_at, projects(name)')
                .in('project_id', myProjectIds).eq('status', 'qs_review')
                .order('raised_at', { ascending: true }),
            ]);
            result.variations = vars || [];
            result.variationsToPrice = varsToPrice || [];
          }

          if (role === 'mc_qs') {
            const { data: submittedDW } = await supabase.from('dayworks')
              .select('id, reference, date_of_works, status, project_id, projects(name)')
              .in('project_id', myProjectIds).eq('status', 'submitted')
              .order('submitted_at', { ascending: false }).limit(10);
            result.submittedDayworks = submittedDW || [];
          }

          const { data: disputed } = await supabase.from('dayworks')
            .select('id, reference, date_of_works, status, dispute_reason, project_id, projects(name)')
            .in('project_id', myProjectIds).eq('status', 'disputed')
            .order('created_at', { ascending: false }).limit(10);
          result.disputedDayworks = disputed || [];

          if (role === 'proline_sm' || role === 'proline_qs') {
            const [
              { count: awaitingMc },
              { count: mcResponded },
              { count: draftIns },
              { count: raisedByMc },
            ] = await Promise.all([
              supabase.from('instructions').select('id', { count: 'exact', head: true })
                .in('type', ['EWN', 'CVI']).eq('status', 'awaiting_instruction').in('project_id', myProjectIds),
              supabase.from('instructions').select('id', { count: 'exact', head: true })
                .in('type', ['EWN', 'CVI']).not('mc_response', 'is', null).eq('status', 'awaiting_instruction').in('project_id', myProjectIds),
              supabase.from('instructions').select('id', { count: 'exact', head: true })
                .eq('status', 'draft').in('project_id', myProjectIds),
              supabase.from('instructions').select('id', { count: 'exact', head: true })
                .eq('raised_by_mc', true).in('project_id', myProjectIds),
            ]);
            result.awaitingMcCount = awaitingMc || 0;
            result.mcRespondedCount = mcResponded || 0;
            result.draftInstructionsCount = draftIns || 0;
            result.raisedByMcCount = raisedByMc || 0;
          }
        }
      }

      setData(result);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div style={S.spinner}>
        {showRefreshBtn ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: C.textMuted, marginBottom: '16px', fontSize: '14px' }}>
              Taking longer than expected.
            </div>
            <button
              onClick={() => { setRetryCount(0); setShowRefreshBtn(false); loadData(); }}
              style={{ ...S.btn, ...S.btnPrimary }}
            >
              Refresh Dashboard
            </button>
          </div>
        ) : (
          'Loading dashboard...'
        )}
      </div>
    );
  }

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div style={S.pageContainer}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ ...S.pageTitle, marginBottom: '4px' }}>
          {greeting()}, {profile?.full_name?.split(' ')[0] || 'User'}
        </h1>
        <p style={{ color: C.textMuted, fontSize: '14px', margin: 0 }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {/* Stats */}
      <div style={S.statsGrid}>
        <StatCard label="My Projects" value={data.projects.length} color={C.accent} />
        {(role === 'proline_sm' || role === 'admin') && (
          <>
            <StatCard label="Open Instructions" value={data.openInstructions.length} color={C.status.open} />
            <StatCard label="Draft Dayworks" value={data.draftDayworks.length} color={C.status.draft} />
          </>
        )}
        {role === 'mc_qs' && (
          <StatCard label="Awaiting Signature" value={data.submittedDayworks.length} color={C.status.submitted} />
        )}
        {(role === 'proline_qs' || role === 'admin') && (
          <StatCard label="Active Variations" value={data.variations.length} color={C.status.priced} />
        )}
        {(role === 'proline_qs' || role === 'admin') && data.variationsToPrice.length > 0 && (
          <StatCard
            label="Variations to Price"
            value={data.variationsToPrice.length}
            color={C.warning}
            bgColor={C.warningLight}
            onClick={() => {}}
          />
        )}
        <StatCard label="Disputed" value={data.disputedDayworks.length} color={C.status.disputed} />
        {role === 'admin' && (
          <StatCard label="Pending Approvals" value={data.pendingApprovals.length} color={C.warning} />
        )}
        {(role === 'admin' || role === 'proline_sm' || role === 'proline_qs') && (
          <>
            <StatCard
              label="Draft Instructions"
              value={data.draftInstructionsCount}
              color={C.status.draft}
            />
            <StatCard
              label="Awaiting MC Instruction"
              value={data.awaitingMcCount}
              color={C.status.submitted}
              bgColor={data.awaitingMcCount > 0 ? C.warningLight : undefined}
              onClick={() => navigate('projects', {})}
            />
            {data.mcRespondedCount > 0 && (
              <StatCard
                label="EWN/CVI — MC Responded"
                value={data.mcRespondedCount}
                color={C.success}
                bgColor={C.successLight}
                onClick={() => navigate('projects', {})}
              />
            )}
            {data.raisedByMcCount > 0 && (
              <StatCard
                label="MC Raised Instructions"
                value={data.raisedByMcCount}
                color='#0ea5e9'
                bgColor='#f0f9ff'
                onClick={() => navigate('projects', {})}
              />
            )}
          </>
        )}
      </div>

      {/* Action sections */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>

        {/* Pending approvals (admin) */}
        {role === 'admin' && (
          <SectionCard
            title={`Pending Approvals (${data.pendingApprovals.length})`}
            emptyMsg="No pending approvals"
          >
            {data.pendingApprovals.length > 0 ? data.pendingApprovals.map(u => (
              <ActionItem
                key={u.id}
                title={u.full_name}
                subtitle={`${u.email} — ${u.role?.replace('_', ' ')}`}
                onClick={() => navigate('admin', { tab: 'approvals' })}
              />
            )) : (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: C.textMuted, fontSize: '14px' }}>
                No pending approvals
              </div>
            )}
          </SectionCard>
        )}

        {/* Open instructions (sm/admin) */}
        {(role === 'proline_sm' || role === 'admin') && (
          <SectionCard
            title={`Open Instructions (${data.openInstructions.length})`}
            emptyMsg="No open instructions"
          >
            {data.openInstructions.length > 0 ? data.openInstructions.slice(0, 5).map(ins => (
              <ActionItem
                key={ins.id}
                title={`${ins.reference} — ${ins.type}`}
                subtitle={ins.projects?.name + ' · ' + (ins.description?.substring(0, 60) || '')}
                status={ins.status}
                onClick={() => navigate('instruction-detail', { instructionId: ins.id })}
              />
            )) : (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: C.textMuted, fontSize: '14px' }}>
                No open instructions
              </div>
            )}
          </SectionCard>
        )}

        {/* Draft dayworks (sm/admin) */}
        {(role === 'proline_sm' || role === 'admin') && data.draftDayworks.length > 0 && (
          <SectionCard title={`Draft Dayworks (${data.draftDayworks.length})`}>
            {data.draftDayworks.slice(0, 5).map(dw => (
              <ActionItem
                key={dw.id}
                title={`${dw.reference}`}
                subtitle={`${dw.projects?.name} · ${formatDate(dw.date_of_works)}`}
                status={dw.status}
                onClick={() => navigate('daywork-detail', { dayworkId: dw.id })}
              />
            ))}
          </SectionCard>
        )}

        {/* MC QS: submitted dayworks */}
        {role === 'mc_qs' && (
          <SectionCard
            title={`Dayworks Awaiting Signature (${data.submittedDayworks.length})`}
            emptyMsg="No dayworks awaiting signature"
          >
            {data.submittedDayworks.length > 0 ? data.submittedDayworks.map(dw => (
              <ActionItem
                key={dw.id}
                title={`${dw.reference}`}
                subtitle={`${dw.projects?.name} · ${formatDate(dw.date_of_works)}`}
                status={dw.status}
                onClick={() => navigate('daywork-detail', { dayworkId: dw.id })}
              />
            )) : (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: C.textMuted, fontSize: '14px' }}>
                No dayworks awaiting signature
              </div>
            )}
          </SectionCard>
        )}

        {/* Disputed dayworks (all) */}
        {data.disputedDayworks.length > 0 && (
          <SectionCard title={`Disputed Dayworks (${data.disputedDayworks.length})`}>
            {data.disputedDayworks.map(dw => (
              <ActionItem
                key={dw.id}
                title={`${dw.reference}`}
                subtitle={`${dw.projects?.name} · ${dw.dispute_reason?.substring(0, 50) || ''}`}
                status={dw.status}
                onClick={() => navigate('daywork-detail', { dayworkId: dw.id })}
              />
            ))}
          </SectionCard>
        )}

        {/* Variations requiring QS pricing */}
        {(role === 'proline_qs' || role === 'admin') && data.variationsToPrice.length > 0 && (
          <SectionCard
            title={`Variations Requiring Pricing (${data.variationsToPrice.length})`}
            emptyMsg="No variations awaiting pricing"
          >
            {data.variationsToPrice.map(v => (
              <ActionItem
                key={v.id}
                title={`${v.reference} — ${v.title || 'Untitled'}`}
                subtitle={`${v.projects?.name}${v.raised_at ? ` · Raised ${formatDate(v.raised_at)}` : ''}`}
                status="qs_review"
                onClick={() => navigate('variation-detail', { variationId: v.id })}
              />
            ))}
          </SectionCard>
        )}

        {/* Variations (qs/admin) */}
        {(role === 'proline_qs' || role === 'admin') && (
          <SectionCard
            title={`Variations Needing Attention (${data.variations.length})`}
            emptyMsg="No variations requiring action"
          >
            {data.variations.length > 0 ? data.variations.slice(0, 5).map(v => (
              <ActionItem
                key={v.id}
                title={`${v.reference} — ${v.title}`}
                subtitle={v.projects?.name}
                status={v.status}
                onClick={() => navigate('variation-detail', { variationId: v.id })}
              />
            )) : (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: C.textMuted, fontSize: '14px' }}>
                No variations requiring action
              </div>
            )}
          </SectionCard>
        )}

        {/* My Projects */}
        <SectionCard title={`My Projects (${data.projects.length})`} emptyMsg="No projects assigned">
          {data.projects.length > 0 ? data.projects.map(p => (
            <ActionItem
              key={p.id}
              title={p.name}
              subtitle={p.reference}
              onClick={() => navigate('project-detail', { projectId: p.id })}
            />
          )) : (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: C.textMuted, fontSize: '14px' }}>
              No projects assigned yet
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
